import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { axiosNewInstance } from "../../config/Api";
import { setReturnalCalculatorData } from "../../actions/Utils";
import { policyNameId, formatToYmd, mapRelationToCode, cleanMode } from "../../utils/utils";

const PolicySubmitByDocumentUpload = () => {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("MIXED");
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();

  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFileError("");
    if (selectedFile) {
      if (selectedFile.type !== "application/pdf") {
        const msg = "Please upload a PDF file.";
        setFileError(msg);
        toast.error(msg);
        setFile(null);
        e.target.value = null;
        return;
      }
      if (selectedFile.size > MAX_FILE_SIZE) {
        const msg = "File size exceeds 2 MB. Maximum allowed size is 2 MB.";
        setFileError(msg);
        toast.error(msg);
        setFile(null);
        e.target.value = null;
        return;
      }
      setFile(selectedFile);
    } else {
      setFile(null);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleOcrResponse = async (data, base64File) => {
    try {
      if (data.status === 'completed') {
        if (data.data?.errorType || data.data?.errorMessage) {
          throw new Error(data.data.errorMessage || "OCR Engine failed to process the document.");
        }

        if (data.data?.statusCode && data.data.statusCode !== 200) {
          let errDetail = "";
          try {
            const parsedBody = typeof data.data.body === "string" ? JSON.parse(data.data.body) : data.data.body;
            errDetail = parsedBody?.error || parsedBody?.message || "";
          } catch (e) {}
          throw new Error(errDetail || `OCR extraction failed (status code ${data.data.statusCode})`);
        }

        const extraction = data.data?.body ? (typeof data.data.body === "string" ? JSON.parse(data.data.body)?.extraction : data.data.body?.extraction) : data.data?.extraction;

        if (!extraction) {
          throw new Error("Failed to extract data from document");
        }

        toast.success("Extraction successful!");

        // Map OCR to Redux State
        const proposer = extraction.proposer_details || {};
        const plan = extraction.pf_plan_details || {};
        const nom = plan.nominee_details || {};

        const proposerDob = formatToYmd(proposer.dob || "");
        const nomineeDob = formatToYmd(nom.dob || "");
        const docDate = formatToYmd(plan.date_of_commencement || "");

        const calcAge = (dob) => {
          if (!dob) return "";
          const parts = dob.split("-");
          if (parts.length !== 3) return "";
          const bd = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
          const td = new Date();
          let a = td.getFullYear() - bd.getFullYear();
          const m = td.getMonth() - bd.getMonth();
          if (m < 0 || (m === 0 && td.getDate() < bd.getDate())) a--;
          return a > 0 ? String(a) : "";
        };

        const finalAge = proposer.age || calcAge(proposerDob) || "";
        const nomRelation = mapRelationToCode(nom.relation || "");

        // Extract numeric plan code from OCR value like "809 & 6" → "809"
        const rawPlanCode = plan.plan || "";
        const planCodeMatch = String(rawPlanCode).match(/\d+/);
        const planCode = planCodeMatch ? planCodeMatch[0] : "";
        const planName = policyNameId[parseInt(planCode)] || "";

        const mappedMode = cleanMode(plan.mode);

        const rawTerm = plan.term || "";
        const termMatch = String(rawTerm).match(/\d+/);
        const termCode = termMatch ? termMatch[0] : "";
        const rawPpt = plan.premium_paying_term || "";
        const pptMatch = String(rawPpt).match(/\d+/);
        const pptCode = pptMatch ? pptMatch[0] : termCode;

        const mapOccupationToCode = (raw) => {
          if (!raw) return "10";
          const str = String(raw).trim().toLowerCase();
          if (["10", "60", "63", "70"].includes(str)) return str;
          if (str.includes("service") || str.includes("job") || str.includes("govt") || str.includes("private") || str.includes("officer") || str.includes("employee") || str.includes("staff")) return "10";
          if (str.includes("business") || str.includes("trade") || str.includes("merchant") || str.includes("shop") || str.includes("commerce")) return "60";
          if (str.includes("agri") || str.includes("farm") || str.includes("cultiv")) return "63";
          if (str.includes("other") || str.includes("housewife") || str.includes("student") || str.includes("retired") || str.includes("lawyer") || str.includes("doctor")) return "70";
          return "10";
        };

        const mapAnnualIncomeToCode = (raw) => {
          if (!raw) return "1";
          const str = String(raw).trim();
          if (["1", "2", "3"].includes(str)) return str;
          const num = parseFloat(str.replace(/,/g, ""));
          if (!isNaN(num)) {
            if (num <= 500000) return "1";
            if (num <= 1000000) return "2";
            return "3";
          }
          const lower = str.toLowerCase();
          if (lower.includes("500001") || lower.includes("1000000")) return "2";
          if (lower.includes("above") || lower.includes("1000001")) return "3";
          return "1";
        };

        const mapEducationToCode = (raw) => {
          if (!raw) return "S";
          const str = String(raw).trim();
          const upper = str.toUpperCase();
          if (["S", "I", "G", "P", "O"].includes(upper)) return upper;
          if (upper.includes("SSC") || upper.includes("MATRIC") || upper.includes("SECONDARY") || upper.includes("TEN") || upper.includes("10")) return "S";
          if (upper.includes("HSC") || upper.includes("INTER") || upper.includes("HIGHER") || upper.includes("12")) return "I";
          if (upper.includes("GRAD") || upper.includes("BSC") || upper.includes("B.SC") || upper.includes("BA") || upper.includes("B.A") || upper.includes("BBA") || upper.includes("DEGREE") || upper.includes("HONOURS") || upper.includes("BACHELOR")) return "G";
          if (upper.includes("POST") || upper.includes("MASTER") || upper.includes("MSC") || upper.includes("M.SC") || upper.includes("MA") || upper.includes("MBA")) return "P";
          if (upper.includes("OTHER")) return "O";
          return "S";
        };

        const customer = {
          first_name: proposer.first_name || proposer.proposer_first_name || "",
          last_name: proposer.last_name || proposer.proposer_last_name || "",
          date_of_birth: proposerDob,
          gender: proposer.gender ? (proposer.gender.toUpperCase().startsWith("F") ? "F" : "M") : "",
          national_id: proposer.national_id || "",
          nationality: proposer.nationality || "Bangladeshi",
          place_of_birth: proposer.place_of_birth || "",
          annual_income: mapAnnualIncomeToCode(proposer.annual_income_from_all_sources),
          occupation: mapOccupationToCode(proposer.occupation),
          education: mapEducationToCode(proposer.education),
          father_name: proposer.father_name || "",
          mother_name: proposer.mother_name || "",
          spouse_name: proposer.spouse_name || "",
          marital_status: proposer.marital_status || "",
          mobile: proposer.address_of_communication?.mob || "",
          email: proposer.email || proposer.address_of_communication?.email || "",
          present_address: proposer.address_of_communication?.line1 || "",
          parmanent_address: proposer.permanent_address?.line1 || "",
        };

        const nomAddress = (nom.address || nom.present_address || "").trim();
        const nominee = {
          first_name: nom.first_name || "",
          last_name: nom.last_name || "",
          date_of_birth: nomineeDob,
          national_id: nom.national_id || "",
          relation: nomRelation,
          relation_id: nomRelation,
          mobile: nom.local_phone_number || "",
          present_address: nomAddress || "Same as LA/Proposer",
          address: nomAddress || "Same as LA/Proposer",
        };

        const mappedData = {
          message: "Success",
          age: finalAge,
          sum_assured: plan.sum_assured || "",
          term: termCode,
          mode: mappedMode,
          premium_amount: plan.premium || "",
          dateOf_birth: proposerDob,
          date_of_commencement: docDate,
          policy: planName,
          policyId: planCode,
          customer: customer,
          nominee: nominee,
          payload: {
             gender: customer.gender,
             age: finalAge,
             dob: proposerDob,
             date_of_birth: proposerDob,
             doc: docDate,
             plan: planCode,
             policy: planCode,
             term: termCode,
             mode: mappedMode,
             sum_assured: plan.sum_assured || "",
             propGender: customer.gender,
             premiumPayingTerm: pptCode,
             cirSa: plan.ci_rider_sa || "",
             hcbSa: plan.hcb_rider_sa || "",
             scbSa: plan.scb_rider_sa || "",
             trSa: plan.term_rider_sa || "",
             pwbFlag: "N",
             lf2CirSa: "0",
          }
        };

        dispatch(setReturnalCalculatorData(mappedData));

        // Redirect to policy buy form
        history.push({
          pathname: "/policy-buy",
          state: { ocrDocumentBase64: base64File, fileName: file.name, fileType: file.type, fileSize: file.size }
        });
        setLoading(false);
      } else {
        throw new Error(data.message || "Unknown error occurred during processing.");
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.message || "Failed to process document.";
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setFileError("Please select a PDF document first.");
      toast.error("Please select a PDF document first.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError("File size exceeds 2 MB. Maximum allowed size is 2 MB.");
      toast.error("File size exceeds 2 MB. Maximum allowed size is 2 MB.");
      return;
    }

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append("document", file);
      formData.append("language", language);

      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      };

      const response = await axiosNewInstance.post("/api/elife/proposal/extract-document", formData, config);
      const data = response.data;
      
      if (data.status === 'completed' && data.document_id) {
        // Process data synchronously
        const base64File = await convertFileToBase64(file);
        handleOcrResponse(data, base64File);
      } else {
        throw new Error(data.message || "Failed to extract OCR data.");
      }

    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.message || "Failed to process document.";
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {loading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: "18px 24px",
              minWidth: 320,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontWeight: 600, fontSize: "15px" }}>Uploading and extracting data…</div>
              <div style={{ color: "#6c757d", fontSize: 13, marginTop: 4 }}>
                This may take up to 2 minutes. Please wait.
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="card shadow-sm">
        <div className="card-header" style={{ backgroundColor: "#004e8f", color: "white" }}>
          <h5 className="mb-0">Policy Submit by Document Upload</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label htmlFor="pdf-language-select" className="form-label font-weight-bold">
                Document Language
              </label>
              <select
                id="pdf-language-select"
                className="form-control"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={loading}
              >
                <option value="MIXED">Mixed (English & Bengali)</option>
                <option value="ENGLISH">English</option>
                <option value="BENGALI">Bengali</option>
              </select>
            </div>

            <div className="form-group mb-4">
              <label htmlFor="pdf-upload-input" className="form-label font-weight-bold">
                Upload Policy Document (PDF only)
              </label>
              <input
                type="file"
                id="pdf-upload-input"
                className={`form-control ${fileError ? "is-invalid" : ""}`}
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={loading}
              />
              <small className="form-text text-muted">
                Max size: 2 MB (PDF only)
              </small>
              {fileError && (
                <div className="invalid-feedback d-block font-weight-bold mt-1">
                  {fileError}
                </div>
              )}
            </div>
            
            <button type="submit" className="btn btn-primary px-4" disabled={loading}>
              {loading ? (
                <span>
                  <i className="fa fa-spinner fa-spin mr-2"></i> Processing...
                </span>
              ) : (
                "Extract & Continue"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PolicySubmitByDocumentUpload;
